import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, Trash2, X, UserCog, Loader2, AlertCircle, DollarSign, ChevronRight, Eye, EyeOff, Lock, Unlock, Scale } from 'lucide-react';
import { getAdmins, addAdmin, updateAdmin, deleteAdmin, toggleLockAdmin, toggleBalanceCheckAdmin } from '../actions/adminActions';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import { fetchCompanies } from '../actions/companyActions';
import { getGames } from '../actions/gameActions';
import GameSelection from './GameSelection';
import BalanceTransferModal from '../components/BalanceTransferModal';
import TableSearchBar from '../components/TableSearchBar';
import styles from '../styles/AdminManagement.module.css';
import { usePrivilege } from '../hooks/usePrivilege';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { useAppToast } from '../hooks/useAppToast';

const AdminManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { can } = usePrivilege();
  const { canShowField } = useFieldPrivilege();
  const { showSuccess, showError, showApiError } = useAppToast();
  const { admins, loading, error } = useSelector(state => state.admin);
  const { companies } = useSelector(state => state.company);
  const games = useSelector(state => state.game.games);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [currentAdmin, setCurrentAdmin] = useState({
    username: '',
    password: '',
    company_id: '',
    gameIds: [],
    is_active: true
  });
  const [availableGames, setAvailableGames] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAdmins = useMemo(() => {
    const list = (admins || []).filter(Boolean);
    if (!searchQuery.trim()) return list;
    const q = searchQuery.trim().toLowerCase();
    return list.filter((admin) => {
      const username = (admin.username || '').toLowerCase();
      const companyName = (companies.find(c => c._id === admin.company_id)?.name || '').toLowerCase();
      const status = admin.is_active ? 'active' : 'inactive';
      return username.includes(q) || companyName.includes(q) || status.includes(q);
    });
  }, [admins, companies, searchQuery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getAdmins()),
          dispatch(fetchCompanies()),
          dispatch(getGames())
        ]);
      } catch (err) {
        showApiError(err, 'Error fetching data');
      }
    };
    fetchData();
  }, [dispatch, showApiError]);

  const handleOpenTransferModal = (toRole, toId, recipient = {}) => {
    setTransferDetails({
      toRole,
      toId,
      recipientBalance: recipient.balance,
      recipientBalanceCheck: recipient.balance_check,
    });
    setIsTransferOpen(true);
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: 'user',
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount,
    });
    const data = throwUnlessApiSuccess(response);
    showSuccess('Balance transferred successfully.');
    return data;
  };

  const handleToggleBalanceCheck = useCallback((adminId) => {
    dispatch(toggleBalanceCheckAdmin(adminId))
      .then((res) => {
        showSuccess(res?.message || 'Balance check updated.');
      })
      .catch((err) => {
        showApiError(err, 'Failed to update balance check.');
      });
  }, [dispatch, showSuccess, showApiError]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setCurrentAdmin(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const togglePasswordVisibility = useCallback((adminId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [adminId]: !prev[adminId],
    }));
  }, []);

  const getMaskedPassword = useCallback((password) => {
    if (!password) return '-';
    // Keep it short for UI; avoid huge strings
    const maskedLen = Math.min(String(password).length, 12);
    return '*'.repeat(maskedLen);
  }, []);

  const handleCompanyChange = useCallback((e) => {
    const companyId = e.target.value;
    setCurrentAdmin(prev => ({ ...prev, company_id: companyId, gameIds: [] }));
    const selectedCompany = companies.find(company => company._id === companyId);
    const filteredGames = games.filter(game => selectedCompany && selectedCompany.gameIds.includes(game._id));
    setAvailableGames(filteredGames);
  }, [companies, games]);

  const handleGameSelection = useCallback((gameId) => {
    setCurrentAdmin(prev => ({
      ...prev,
      gameIds: prev.gameIds.includes(gameId.toString())
        ? prev.gameIds.filter(id => id !== gameId.toString())
        : [...prev.gameIds, gameId.toString()]
    }));
  }, []);

  const handleAddAdmin = useCallback(() => {
    setIsEditing(false);
    setCurrentAdmin({
      username: '',
      password: '',
      company_id: '',
      gameIds: [],
      is_active: true
    });
    setAvailableGames([]);
    setIsModalOpen(true);
  }, []);

  const handleEditAdmin = useCallback((admin) => {
    setIsEditing(true);
    setCurrentAdmin({
      ...admin,
      password: '',
      gameIds: admin.gameIds ? admin.gameIds.map(id => id.toString()) : []
    });
    const selectedCompany = companies.find(company => company._id === admin.company_id);
    const filteredGames = games.filter(game => selectedCompany && selectedCompany.gameIds.includes(game._id));
    setAvailableGames(filteredGames);
    setIsModalOpen(true);
  }, [companies, games]);

  const handleDeleteAdmin = useCallback((adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      dispatch(deleteAdmin(adminId))
        .then(() => {
          showSuccess('Admin deleted successfully.');
          dispatch(getAdmins());
        })
        .catch((error) => {
          showApiError(error, 'Error deleting admin');
        });
    }
  }, [dispatch, showSuccess, showApiError]);

  const handleToggleLock = useCallback((adminId, isLocked) => {
    dispatch(toggleLockAdmin(adminId))
      .then(() => {
        showSuccess(`Admin ${isLocked ? 'unlocked' : 'locked'} successfully.`);
        dispatch(getAdmins());
      })
      .catch((error) => {
        showApiError(error, 'Error updating lock status');
      });
  }, [dispatch, showSuccess, showApiError]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await dispatch(updateAdmin(currentAdmin._id, currentAdmin));
        showSuccess('Admin updated successfully.');
      } else {
        await dispatch(addAdmin(currentAdmin));
        showSuccess('Admin added successfully.');
      }
      setIsModalOpen(false);
      dispatch(getAdmins());
    } catch (error) {
      showApiError(error, `Error ${isEditing ? 'updating' : 'adding'} admin`);
    }
  }, [dispatch, isEditing, currentAdmin, showSuccess, showApiError]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentAdmin({
      username: '',
      password: '',
      company_id: '',
      gameIds: [],
      is_active: true
    });
    setAvailableGames([]);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }

  if (error && admins.length === 0) {
    return (
      <div className={styles.errorAlert}>
        <AlertCircle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.adminManagement}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <UserCog size={24} className={styles.headerIcon} />
          <h1 className={styles.title}>Admin Management</h1>
        </div>
        {can('admin.create') && (
        <button className={styles.addButton} onClick={handleAddAdmin}>
          <Plus size={20} />
          <span>Add Admin</span>
        </button>
        )}
      </div>

      <div className={styles.searchBarRow}>
        <TableSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by username, company, status..."
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {canShowField('admin', 'username', 'list') && <th>Username</th>}
              {canShowField('admin', 'company_id', 'list') && <th>Company</th>}
              {canShowField('admin', 'balance', 'list') && <th>Balance</th>}
              {canShowField('admin', 'balance_check', 'list') && <th>Bal. check</th>}
              {canShowField('admin', 'is_active', 'list') && <th>Status</th>}
              {canShowField('admin', 'password', 'list') && <th>Password</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    1 +
                    ['username', 'company_id', 'balance', 'balance_check', 'is_active', 'password'].filter((f) =>
                      canShowField('admin', f, 'list')
                    ).length
                  }
                  className={styles.emptyState}
                >
                  {admins.length === 0
                    ? 'No admins found. Click "Add Admin" to create one.'
                    : 'No matching admins. Try a different search.'}
                </td>
              </tr>
            ) : (
              filteredAdmins.map((admin) => (
                <tr key={admin._id}>
                  {canShowField('admin', 'username', 'list') && <td>{admin.username || '-'}</td>}
                  {canShowField('admin', 'company_id', 'list') && (
                    <td>
                      {companies.find(c => c._id === admin.company_id)?.name || '-'}
                    </td>
                  )}
                  {canShowField('admin', 'balance', 'list') && <td>{admin.balance || 0}</td>}
                  {canShowField('admin', 'balance_check', 'list') && (
                    <td>
                      {can('admin.balanceCheck') && (
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => handleToggleBalanceCheck(admin._id)}
                        title={admin.balance_check !== false ? 'Balance check ON (no negative transfers)' : 'Balance check OFF'}
                        aria-label="Toggle balance check"
                      >
                        <Scale size={18} className={admin.balance_check !== false ? styles.balanceCheckOn : styles.balanceCheckOff} />
                      </button>
                      )}
                    </td>
                  )}
                  {canShowField('admin', 'is_active', 'list') && (
                    <td>
                      <span className={`${styles.statusBadge} ${admin.is_active ? styles.statusActive : styles.statusInactive}`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  {canShowField('admin', 'password', 'list') && (
                    <td>
                      <div className={styles.passwordCell}>
                        <span className={styles.passwordValue}>
                          {visiblePasswords[admin._id]
                            ? (admin.password || '-')
                            : getMaskedPassword(admin.password)}
                        </span>
                        <button
                          type="button"
                          className={styles.iconButton}
                          onClick={() => togglePasswordVisibility(admin._id)}
                          title={visiblePasswords[admin._id] ? 'Hide password' : 'Show password'}
                          aria-label={visiblePasswords[admin._id] ? 'Hide password' : 'Show password'}
                        >
                          {visiblePasswords[admin._id] ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </td>
                  )}
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        className={styles.iconButton}
                        onClick={() => {
                          const base = slug ? `/${slug}/super-stokez` : '/super-stokez';
                          const params = new URLSearchParams({ admin: admin.username || admin._id });
                          navigate(`${base}?${params.toString()}`);
                        }}
                        title="Go to Super Stokez"
                        aria-label="Go to Super Stokez"
                      >
                        <ChevronRight size={18} />
                      </button>
                      {can('admin.lock') && (
                      <button
                        className={styles.iconButton}
                        onClick={() => handleToggleLock(admin._id, admin.isLocked)}
                        title={admin.isLocked ? 'Unlock' : 'Lock'}
                        aria-label={admin.isLocked ? 'Unlock' : 'Lock'}
                      >
                        {admin.isLocked ? <Lock size={18} /> : <Unlock size={18} />}
                      </button>
                      )}
                      {can('admin.edit') && (
                      <button
                        className={styles.iconButton}
                        onClick={() => handleEditAdmin(admin)}
                        title="Edit"
                        aria-label="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      )}
                      {can('admin.delete') && (
                      <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => handleDeleteAdmin(admin._id)}
                        title="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                      )}
                      {can('admin.transfer') && (
                      <button
                        className={styles.iconButton}
                        onClick={() => handleOpenTransferModal('admin', admin.username, admin)}
                        title="Transfer Balance"
                        aria-label="Transfer Balance"
                      >
                        <DollarSign size={18} />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditing ? 'Edit Admin' : 'Add New Admin'}
              </h2>
              <button className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                {canShowField('admin', 'username', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Username <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={currentAdmin.username}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
                )}
                {canShowField('admin', 'password', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Password {isEditing && <span className={styles.helpText}>(Leave blank to keep current password)</span>}
                    {!isEditing && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={currentAdmin.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    required={!isEditing}
                  />
                </div>
                )}
                {canShowField('admin', 'company_id', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Company <span className={styles.required}>*</span>
                  </label>
                  <select
                    name="company_id"
                    value={currentAdmin.company_id}
                    onChange={handleCompanyChange}
                    className={styles.select}
                    required
                  >
                    <option value="">Select a company</option>
                    {companies.map(company => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                {canShowField('admin', 'is_active', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={currentAdmin.is_active}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Active</span>
                  </label>
                </div>
                )}
                {canShowField('admin', 'gameIds', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Games</label>
                  {availableGames.length > 0 ? (
                    <GameSelection
                      games={availableGames}
                      selectedGames={currentAdmin.gameIds}
                      onGameSelection={handleGameSelection}
                      isDisabled={!currentAdmin.company_id}
                    />
                  ) : (
                    <p className={styles.helpText}>No games available for the selected company</p>
                  )}
                </div>
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditing ? 'Update Admin' : 'Add Admin'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BalanceTransferModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        toRole={transferDetails.toRole}
        toId={transferDetails.toId}
        recipientBalance={transferDetails.recipientBalance}
        recipientBalanceCheck={transferDetails.recipientBalanceCheck}
        onTransfer={handleBalanceTransfer}
        onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)}
      />
    </div>
  );
};

export default React.memo(AdminManagement);
